/**
 * CORE-904 — the S3 multipart-upload part grid, as a pure module shared with
 * the backend (mirrored byte-for-byte in `MultipartUploadPolicy.cs`).
 *
 * A single presigned PUT tops out at 5 GiB, so a practice between 5 GiB and the
 * 8 GB product cap (CORE-898, `MAX_VIDEO_UPLOAD_BYTES`) passes both size gates,
 * uploads for tens of minutes, then dies with `400 EntityTooLarge` on the final
 * request — exactly the late failure the client size gate exists to prevent.
 *
 * The fix routes large videos through an S3 multipart upload: the file is sliced
 * into parts, each PUT straight to S3 (still bypassing the shaped origin), and
 * an interrupted upload resumes from the parts S3 already holds. The grid is
 * dictated by three hard S3 limits, not guessed:
 *   - each part is at least 5 MiB (except the last);
 *   - at most 10 000 parts;
 *   - at most 5 TiB per object.
 *
 * So the part size can't be the old chunked 4 MiB (S3 rejects a sub-5-MiB part)
 * nor a fixed 5 MiB (an 8 GB file → 1 600 parts; a 5 TiB object → 1 000 000, over
 * the cap). It's derived from the file size: a comfortable default, grown only
 * when the default would exceed the part cap.
 */

const MIB = 1024 * 1024;

/** S3 minimum size for every part except the last. */
export const MIN_PART_SIZE_BYTES = 5 * MIB;

/** S3 hard cap on the number of parts in one multipart upload. */
export const MAX_PARTS = 10_000;

/** S3 hard cap on the assembled object size (5 TiB). */
export const MAX_OBJECT_SIZE_BYTES = 5 * 1024 * 1024 * MIB;

/**
 * Default part size. 16 MiB keeps an 8 GB practice at ~512 parts — small enough
 * to make resume granular, big enough that a multi-gigabyte file never
 * approaches the 10 000-part cap on its own.
 */
export const DEFAULT_PART_SIZE_BYTES = 16 * MIB;

/**
 * Files at or above this size go through multipart (resume + no 5 GiB ceiling);
 * smaller ones keep the proven single presigned PUT, where a re-upload on a
 * dropped connection is quick and no ceiling is in reach. Well below the 5 GiB
 * single-PUT limit so nothing that could 400 ever takes the single-PUT path.
 */
export const MULTIPART_THRESHOLD_BYTES = 100 * MIB;

export interface PartPlan {
  /** Size of every part except (possibly) the last, in bytes. */
  partSize: number;
  /** Total number of parts. */
  partCount: number;
}

const ceilDiv = (a: number, b: number) => Math.ceil(a / b);

/**
 * Plan the part grid for a file of `fileSizeBytes` bytes. Throws when the file
 * is negative or exceeds the 5 TiB object cap.
 */
export function planMultipartParts(fileSizeBytes: number): PartPlan {
  if (!Number.isFinite(fileSizeBytes) || fileSizeBytes < 0) {
    throw new RangeError(`Invalid file size: ${fileSizeBytes}`);
  }
  if (fileSizeBytes > MAX_OBJECT_SIZE_BYTES) {
    throw new RangeError(`File exceeds the 5 TiB S3 object limit (${fileSizeBytes} bytes).`);
  }
  // An empty file is still a single (zero-length) part.
  if (fileSizeBytes === 0) return { partSize: MIN_PART_SIZE_BYTES, partCount: 1 };

  let partSize = DEFAULT_PART_SIZE_BYTES;
  // Grow the part size only when the default grid would overflow the part cap,
  // then round up to a whole MiB so the number stays tidy.
  if (ceilDiv(fileSizeBytes, partSize) > MAX_PARTS) {
    partSize = ceilDiv(ceilDiv(fileSizeBytes, MAX_PARTS), MIB) * MIB;
  }
  if (partSize < MIN_PART_SIZE_BYTES) partSize = MIN_PART_SIZE_BYTES;

  const partCount = ceilDiv(fileSizeBytes, partSize);
  return { partSize, partCount };
}

/**
 * Whether a file of this size should upload via S3 multipart (vs the single
 * presigned PUT). A file whose size is unknown (native `{uri}` before the blob
 * is read) returns `false` — the caller resolves the blob and re-checks.
 */
export function shouldUseMultipart(fileSizeBytes: number | null | undefined): boolean {
  return typeof fileSizeBytes === 'number' && fileSizeBytes >= MULTIPART_THRESHOLD_BYTES;
}
